import type { PAYMENT_STATUS } from '../enums';
import type { CustomOutcomeType } from '../types';
import type { Toast } from '@Atoms';
import type { RouteLocationNormalized, NavigationGuardNext } from 'vue-router';
import { useAToast } from '@Atoms';
// NOTE :: Imported by path, not from '../helpers'. The module has BOTH a helpers.ts file and a
// helpers/ directory, and the file shadows the directory — '../helpers' never reaches the folder.
import { getCartRouteStage } from '../helpers/getCartRouteStage.helper';
import { GetPendingOutcomeService } from '../services';
import useCartStore from '../store';

type CartGuardType = 'completed' | 'pending';

/**
 * Route guards for shopping cart views.
 *
 * Creates beforeEnter guards that validate cart status before allowing navigation.
 * If the cart status doesn't match the expected route type, the user is redirected
 * to the appropriate view or shown an error toast.
 *
 * @example
 * const { createCartGuard } = useCartRouteGuards();
 * const completedCartGuard = createCartGuard('completed');
 * const pendingCartGuard = createCartGuard('pending');
 *
 * // In routes.ts:
 * { path: 'outcome-completed/:id', beforeEnter: completedCartGuard }
 * { path: 'outcome-pending/:id', beforeEnter: pendingCartGuard }
 */
export default function useCartRouteGuards() {
  const FALLBACK_ROUTE = '/shelter/animals/in-care';

  function createCartGuard(type: CartGuardType) {
    return (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext): void => {
      const cartId = to.params.id as string;
      const { notify, TOAST_TYPES } = useAToast();
      const TOAST_INVALID_CART: Toast = {
        message: 'cart.toast.invalidCart.error.message',
        title: 'cart.toast.invalidCart.error.title',
        type: TOAST_TYPES.ERROR,
      };

      if (!cartId) {
        notify(TOAST_INVALID_CART);
        return next(FALLBACK_ROUTE);
      }

      const store = useCartStore();

      GetPendingOutcomeService(cartId)
        .then((data) => {
          if (!data?._id) {
            notify(TOAST_INVALID_CART);
            return next(FALLBACK_ROUTE);
          }

          to.meta.cartData = data;

          if (type === 'completed') {
            return handleCompletedValidation(data, cartId, to, next, store, notify, TOAST_INVALID_CART);
          }

          return handlePendingValidation(data, cartId, to, next, store, notify, TOAST_INVALID_CART);
        })
        .catch(() => {
          notify(TOAST_INVALID_CART);
          return next(FALLBACK_ROUTE);
        });
    };
  }

  function handleCompletedValidation(
    data: { status: PAYMENT_STATUS; cart_type: string; custom_outcome_type?: CustomOutcomeType },
    cartId: string,
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
    store: ReturnType<typeof useCartStore>,
    notify: ReturnType<typeof useAToast>['notify'],
    TOAST_INVALID_CART: Toast
  ): void {
    if (data?.custom_outcome_type) return next();

    const stage = getCartRouteStage(data.status);

    if (stage === 'completed') return proceedAndClearStaleCart(to, next, store);

    if (stage === 'active') return next(store.getActiveOutcomeRoute(cartId, data.cart_type));

    if (stage === 'pending') return next(store.getPendingOutcomeRoute(cartId, data.cart_type));

    notify(TOAST_INVALID_CART);
    return next(FALLBACK_ROUTE);
  }

  function handlePendingValidation(
    data: {
      status: PAYMENT_STATUS;
      cart_type: string;
      foster_animal_unavailable?: boolean;
      foster_unavailable_animal_id?: string;
    },
    cartId: string,
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
    store: ReturnType<typeof useCartStore>,
    notify: ReturnType<typeof useAToast>['notify'],
    TOAST_INVALID_CART: Toast
  ): void {
    const fosterAnimalUnavailable = data?.foster_animal_unavailable && !!data?.foster_unavailable_animal_id;
    if (fosterAnimalUnavailable) return next(`/shelter/animal-profile/${data.foster_unavailable_animal_id}`);

    const stage = getCartRouteStage(data.status);

    if (stage === 'pending') return proceedAndClearStaleCart(to, next, store);
    if (stage === 'active') return next(store.getActiveOutcomeRoute(cartId, data.cart_type));
    if (stage === 'completed') return next(store.getCompletedOutcomeRoute(cartId, data.cart_type));

    notify(TOAST_INVALID_CART);
    return next(FALLBACK_ROUTE);
  }

  function proceedAndClearStaleCart(
    to: RouteLocationNormalized,
    next: NavigationGuardNext,
    store: ReturnType<typeof useCartStore>
  ): void {
    const routeCartId = to.params.id as string;
    const storeCartId = (store.cartId as { $oid?: string })?.$oid ?? store.cartId ?? '';
    const sameId = routeCartId === storeCartId && !!storeCartId;

    next();

    if (sameId) {
      const amountOfTimeToAvoidBlinkEmptyState = 50;
      setTimeout(() => store.resetCart(), amountOfTimeToAvoidBlinkEmptyState);
    }
  }

  return { createCartGuard };
}
